import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AuthContext } from "@/auth/AuthContext";
import { keycloakClient } from "@/auth/keycloak";
import { AuthApi, AuthState } from "@/auth/auth.types";
import { authLogic } from "@/auth/auth.logic";

const REFRESH_BUFFER_SECONDS = 30;
const REFRESH_INTERVAL_MS = 20_000;

type Props = { children: ReactNode };

export const AuthProvider: React.FC<Props> = ({ children }) => {
    const [state, setState] = useState<AuthState>({
        status: "initializing",
        user: null,
        realmRoles: [],
        clientRoles: [],
        error: null,
    });
    const initialized = useRef(false);

    const refreshState = useCallback((status: AuthState["status"]) => {
        const tokenParsed = keycloakClient.instance.tokenParsed;
        setState({
            status,
            user: status === "authenticated" ? authLogic.parseUser(tokenParsed) : null,
            realmRoles: authLogic.parseRealmRoles(tokenParsed),
            clientRoles: authLogic.parseClientRoles(tokenParsed),
            error: null,
        });
    }, []);

    useEffect(() => {
        if (initialized.current) {
            console.log("[metrics-auth] init skipped (already initialized)");
            return;
        }
        initialized.current = true;

        console.log("[metrics-auth] calling keycloak.init()", {
            url: keycloakClient.instance.authServerUrl,
            realm: keycloakClient.instance.realm,
            clientId: keycloakClient.instance.clientId,
            currentHref: window.location.href,
        });

        keycloakClient.instance.onTokenExpired = () => {
            console.log("[metrics-auth] onTokenExpired");
            keycloakClient.instance
                .updateToken(REFRESH_BUFFER_SECONDS)
                .catch(() => {
                    console.warn("[metrics-auth] refresh failed -> login()");
                    keycloakClient.instance.login();
                });
        };

        keycloakClient.instance
            .init({
                onLoad: "login-required",
                checkLoginIframe: false,
                redirectUri: window.location.href,
            })
            .then((authenticated) => {
                console.log("[metrics-auth] init resolved", { authenticated });
                refreshState(authenticated ? "authenticated" : "unauthenticated");
            })
            .catch((error: unknown) => {
                console.error("[metrics-auth] init failed", error);
                setState({
                    status: "error",
                    user: null,
                    realmRoles: [],
                    clientRoles: [],
                    error: error instanceof Error ? error.message : "Auth init failed",
                });
            });
    }, [refreshState]);

    useEffect(() => {
        if (state.status !== "authenticated") return;
        const intervalId = window.setInterval(() => {
            keycloakClient.instance
                .updateToken(REFRESH_BUFFER_SECONDS)
                .catch(() => {
                    keycloakClient.instance.login();
                });
        }, REFRESH_INTERVAL_MS);
        return () => window.clearInterval(intervalId);
    }, [state.status]);

    const login = useCallback(() => {
        keycloakClient.instance.login();
    }, []);

    const logout = useCallback(() => {
        keycloakClient.instance.logout({ redirectUri: window.location.origin });
    }, []);

    const getToken = useCallback((): string | undefined => {
        return keycloakClient.instance.token;
    }, []);

    const api = useMemo<AuthApi>(
        () => ({
            state,
            login,
            logout,
            getToken,
            keycloak: keycloakClient.instance,
        }),
        [state, login, logout, getToken]
    );

    return <AuthContext.Provider value={api}>{children}</AuthContext.Provider>;
};
